import { useState, useEffect } from 'react'
import { Link } from 'react-router-dom'
import { PublicNavBar } from '../components/PublicNavBar'
import { PublicFooter } from '../components/PublicFooter'
import { LoadingSpinner } from '../components/LoadingSpinner'
import { usePageMeta } from '../hooks/usePageMeta'
import { pageMeta } from '../seo/pageMeta'
import { getCapabilities } from '../api/client'
import type { PlanInfo, PlanLimits } from '../api/client'

// -1 means unlimited; 0 means not included on the plan.
export function formatLimit(n: number): string {
  if (n < 0) return 'Unlimited'
  if (n === 0) return '—'
  return n.toLocaleString()
}

export function formatStorage(bytes: number): string {
  if (bytes < 0) return 'Unlimited'
  if (bytes === 0) return '—'
  return `${Math.round(bytes / 1024 ** 3)} GB`
}

export function formatPrice(cents: number, perSeat: boolean): string {
  return `$${cents / 100}${perSeat ? '/seat' : ''}`
}

interface TierCard {
  id: string
  name: string
  price: string
  bullets: string[]
}

// Headline facts shown when the capabilities endpoint is unavailable.
const FALLBACK_CARDS: TierCard[] = [
  { id: 'free', name: 'Free', price: '$0', bullets: ['5 projects', '1 concurrent agent'] },
  { id: 'pro', name: 'Pro', price: '$19', bullets: ['25 projects', '5 concurrent agents'] },
  { id: 'team', name: 'Team', price: '$49/seat', bullets: ['Unlimited projects', '20 concurrent agents'] },
]

const CARD_ORDER = ['free', 'pro', 'team']

const LIMIT_ROWS: { key: keyof PlanLimits; label: string; format: (n: number) => string }[] = [
  { key: 'max_projects', label: 'Projects', format: formatLimit },
  { key: 'max_agents', label: 'Concurrent agents', format: formatLimit },
  { key: 'max_workflow_executions', label: 'Workflow executions / month', format: formatLimit },
  { key: 'max_test_runs', label: 'Test runs / month', format: formatLimit },
  { key: 'max_code_reviews', label: 'AI code reviews / month', format: formatLimit },
  { key: 'max_agent_minutes_monthly', label: 'Agent minutes / month', format: formatLimit },
  { key: 'max_chat_messages_daily', label: 'Chat messages / day', format: formatLimit },
  { key: 'max_api_calls_daily', label: 'API calls / day', format: formatLimit },
  { key: 'max_storage_bytes', label: 'Storage', format: formatStorage },
]

function toCard(plan: PlanInfo): TierCard {
  return {
    id: plan.id,
    name: plan.name,
    price: formatPrice(plan.price_monthly_cents, plan.per_seat),
    bullets: [
      `${formatLimit(plan.limits.max_projects)} projects`,
      `${formatLimit(plan.limits.max_agents)} concurrent agent${plan.limits.max_agents === 1 ? '' : 's'}`,
      ...(plan.features ?? []),
    ],
  }
}

export default function Pricing() {
  usePageMeta(pageMeta('/pricing'))

  const [plans, setPlans] = useState<PlanInfo[] | null>(null)
  const [loading, setLoading] = useState(true)

  useEffect(() => {
    getCapabilities()
      .then((caps) => setPlans(caps.plans && caps.plans.length > 0 ? caps.plans : null))
      .catch(() => setPlans(null))
      .finally(() => setLoading(false))
  }, [])

  const tierPlans = plans
    ? CARD_ORDER.map((id) => plans.find((p) => p.id === id)).filter((p): p is PlanInfo => !!p)
    : null
  const cards = tierPlans && tierPlans.length > 0 ? tierPlans.map(toCard) : FALLBACK_CARDS

  return (
    <div className="min-h-screen text-gray-900 dark:text-gray-100">
      <PublicNavBar />

      {/* Hero */}
      <section className="px-4 pt-16 sm:pt-24 pb-12">
        <div className="max-w-3xl mx-auto text-center">
          <h1 className="text-3xl sm:text-5xl font-bold mb-4 leading-tight">
            <span className="bg-gradient-to-r from-gray-900 via-gray-700 to-gray-500 dark:from-white dark:via-gray-200 dark:to-gray-400 bg-clip-text text-transparent">
              Simple, honest pricing
            </span>
          </h1>
          <p className="text-lg text-gray-500 dark:text-gray-400 max-w-2xl mx-auto">
            Start free, upgrade when your AI team needs to scale. Every limit is published below — no surprises
            after you pay.
          </p>
        </div>
      </section>

      {/* Tier cards */}
      <section className="px-4 pb-16">
        <div className="max-w-6xl mx-auto">
          {loading ? (
            <div className="flex justify-center py-16">
              <LoadingSpinner size="lg" />
            </div>
          ) : (
            <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6">
              {cards.map((card) => (
                <div
                  key={card.id}
                  className={`bg-white dark:bg-gray-900 rounded-2xl p-6 relative ${
                    card.id === 'pro'
                      ? 'border-2 border-primary-500'
                      : 'border border-gray-200 dark:border-gray-700'
                  }`}
                >
                  {card.id === 'pro' && (
                    <div className="absolute -top-3 left-6 px-3 py-0.5 bg-primary-600 text-white text-xs font-medium rounded-full">
                      Most popular
                    </div>
                  )}
                  <h3 className="text-lg font-semibold mb-1">{card.name}</h3>
                  <p className="text-3xl font-bold mb-4">
                    {card.price}
                    <span className="text-sm font-normal text-gray-400">/mo</span>
                  </p>
                  <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                    {card.bullets.map((bullet) => (
                      <li key={bullet} className="flex items-start gap-2">
                        <svg className="w-4 h-4 text-green-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                        {bullet}
                      </li>
                    ))}
                  </ul>
                  <Link
                    to="/register"
                    className={`block text-center px-4 py-2 font-medium rounded-lg text-sm transition-all ${
                      card.id === 'pro'
                        ? 'bg-gradient-to-r from-primary-600 to-orange-500 text-white hover:from-primary-500 hover:to-orange-400 shadow-lg shadow-orange-500/20'
                        : 'border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 hover:border-gray-400'
                    }`}
                  >
                    {card.id === 'free' ? 'Get started free' : 'Start building'}
                  </Link>
                </div>
              ))}

              {/* Enterprise */}
              <div className="bg-white dark:bg-gray-900 rounded-2xl border border-gray-200 dark:border-gray-700 p-6">
                <h3 className="text-lg font-semibold mb-1">Enterprise</h3>
                <p className="text-3xl font-bold mb-4">Custom</p>
                <ul className="space-y-2 text-sm text-gray-600 dark:text-gray-400 mb-6">
                  {['Unlimited everything', 'Custom limits & SLAs', 'Dedicated support'].map((bullet) => (
                    <li key={bullet} className="flex items-start gap-2">
                      <svg className="w-4 h-4 text-green-500 shrink-0 mt-0.5" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor"><path strokeLinecap="round" strokeLinejoin="round" d="M4.5 12.75l6 6 9-13.5" /></svg>
                      {bullet}
                    </li>
                  ))}
                </ul>
                <a
                  href="mailto:support@notify.factory-nexus.ai"
                  className="block text-center px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 font-medium rounded-lg hover:border-gray-400 transition-all text-sm"
                >
                  Contact us
                </a>
              </div>
            </div>
          )}
        </div>
      </section>

      {/* Comparison table — only when live plan data is available */}
      {tierPlans && tierPlans.length > 0 && (
        <section className="py-16 px-4 bg-gray-50 dark:bg-gray-800/50">
          <div className="max-w-4xl mx-auto">
            <h2 className="text-2xl sm:text-3xl font-bold text-center mb-3">What&apos;s included every month</h2>
            <p className="text-center text-gray-500 dark:text-gray-400 mb-10">
              Every plan comes with a monthly allowance. Hit a cap and everything you&apos;ve built stays put —
              you just wait for the reset or upgrade.
            </p>
            <div className="overflow-x-auto rounded-xl border border-gray-200 dark:border-gray-700">
              <table className="min-w-full divide-y divide-gray-200 dark:divide-gray-700 text-sm">
                <thead className="bg-white dark:bg-gray-900">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-gray-500 dark:text-gray-400"></th>
                    {tierPlans.map((plan) => (
                      <th key={plan.id} className="px-4 py-3 text-left font-semibold text-gray-900 dark:text-gray-100">
                        {plan.name}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody className="bg-white dark:bg-gray-900 divide-y divide-gray-200 dark:divide-gray-700">
                  {LIMIT_ROWS.map((row) => (
                    <tr key={row.key}>
                      <td className="px-4 py-3 font-medium text-gray-700 dark:text-gray-300">{row.label}</td>
                      {tierPlans.map((plan) => (
                        <td key={plan.id} className="px-4 py-3 text-gray-600 dark:text-gray-400">
                          {row.format(plan.limits[row.key])}
                        </td>
                      ))}
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </section>
      )}

      {/* FAQ */}
      <section className="py-16 sm:py-24 px-4">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-2xl sm:text-3xl font-bold text-center mb-10">Pricing FAQ</h2>
          <div className="space-y-4">
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5">
              <h3 className="font-semibold mb-2">What happens when I hit a limit?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                The related feature pauses until the limit resets or you upgrade. Nothing is ever deleted —
                your projects, code, and history stay exactly where they are.
              </p>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5">
              <h3 className="font-semibold mb-2">When do limits reset?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Monthly allowances (agent minutes, workflow executions, test runs, code reviews) reset each
                month. Daily allowances (API calls, chat messages) reset each day. Projects and storage are
                running totals — free up space by deleting what you no longer need.
              </p>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5">
              <h3 className="font-semibold mb-2">What happens to demo projects?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Projects built without an account are automatically deleted after 7 days. Sign up (free) and
                claim a project to keep it.
              </p>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5">
              <h3 className="font-semibold mb-2">Which AI models power the agents?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Factory Nexus routes across multiple providers — Anthropic, OpenRouter-routed models, and
                DigitalOcean inference — with automatic fallback. Read{' '}
                <Link to="/how-we-use-ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">How we use AI</Link>{' '}
                for the full picture.
              </p>
            </div>
            <div className="border border-gray-200 dark:border-gray-700 rounded-xl p-5">
              <h3 className="font-semibold mb-2">How do I set up a Team or Enterprise plan?</h3>
              <p className="text-sm text-gray-600 dark:text-gray-400">
                Email{' '}
                <a href="mailto:support@notify.factory-nexus.ai" className="text-primary-600 dark:text-primary-400 hover:text-primary-500">support@notify.factory-nexus.ai</a>{' '}
                and we&apos;ll get your organization set up.
              </p>
            </div>
          </div>
        </div>
      </section>

      <PublicFooter />
    </div>
  )
}
